/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useEffect, useState } from "react";
import { useFormik } from "formik";
import classNames from "clsx";

import { RxExternalLink } from "react-icons/rx";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { useApi } from "@/hooks/useApi";
import { useSwal } from "@/hooks/useSwal";
import { transformData } from "@/utils/transformData";
import { declarationMap } from "@/utils/mappings/declaration";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { surgicalCenterPage } from "@/utils/initialValues/surgicalCenterPage";

import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { PatientsSelect } from "@/components/Selects/PatientsSelect";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { ReactSelect } from "@/components/Selects/ReactSelect";
import { BedsSelect } from "@/components/Selects/BedsSelect";
import { FormRadio } from "@/components/Radio/FormRadio";

import PdfIcon from "@/assets/images/pdfIcon.png";

import "./styles.scss";

type Props = {
	patientAdmissionModalShow: boolean;
	setPatientAdmissionModalShow: (patientAdmissionModalShow: boolean) => void;
	queueAdmissionData: QueueAdmissionData;
	surgicalTeamsOptions: ReactSelectOption[];
	setIsPatientAdmitted: (isPatientAdmitted: boolean) => void;
	isPatientAdmitted: boolean;
	onPatientAdmitted?: () => void;
};

type DeclarationCardProps = {
	description: string;
	children: ReactNode;
};

export function PatientAdmissionModal({
	patientAdmissionModalShow,
	setPatientAdmissionModalShow,
	queueAdmissionData,
	surgicalTeamsOptions,
	setIsPatientAdmitted,
	isPatientAdmitted,
	onPatientAdmitted,
}: Props) {
	const [isPatientAdmissionModalLoading, setIsPatientAdmissionModalLoading] = useState(false);
	const [attendance, setAttendance] = useState<SurgicalAttendance>({} as SurgicalAttendance);
	const [isDeclarationLoading, setIsDeclarationLoading] = useState(false);
	const { Toast, toastEndpointErrors } = useSwal();
	const { width } = useWindowDimensions();
	const { api } = useApi();

	const isQueueAdmission = queueAdmissionData.isQueueAdmission;
	const expertise = attendance.expertise;
	const bed = queueAdmissionData.bed;

	const handlePatientAdmissionModalClose = () => setPatientAdmissionModalShow(false);

	const DeclarationCard = ({ description, children }: DeclarationCardProps) => (
		<Col lg={6}>
			<div className="d-flex justify-content-between align-items-center border rounded p-3">
				<div className="d-flex align-items-center gap-3">
					<img src={PdfIcon} width="50" alt="ícone de pdf" />
					<p className="fs-5 m-0">{description}</p>
				</div>
				{children}
			</div>
		</Col>
	);

	const formik = useFormik({
		initialValues: surgicalCenterPage,
		onSubmit: async (values) => {
			const id = queueAdmissionData.surgicalAttendanceId || attendance.id;

			if (!isQueueAdmission) {
				values.surgical_bed_id = String(bed?.id);
			}
			values.expertise_id = String(queueAdmissionData.expertiseId || expertise?.id);

			try {
				setIsPatientAdmissionModalLoading(true);
				await api.post(`surgical-center/${id}/admit-patient`, values);

				setIsPatientAdmitted(!isPatientAdmitted);
				handlePatientAdmissionModalClose();
				setIsPatientAdmissionModalLoading(false);

				formik.setFieldValue("procedure_size", "");
				formik.setFieldValue("surgery_date", "");
				formik.setFieldValue("surgical_bed_id", "");

				if (onPatientAdmitted) {
					onPatientAdmitted();
				}

				Toast.fire({
					icon: "success",
					title: "Paciente admitido com sucesso",
				});
			} catch (error: any) {
				console.log(error);
				toastEndpointErrors(error.response.data?.errors);
				setIsPatientAdmissionModalLoading(false);
			}
		},
	});

	async function getAllDeclarations() {
		try {
			setIsDeclarationLoading(true);
			const id = queueAdmissionData.attendanceId || attendance.attendanceId;

			const { data } = await api.get(`surgical-center/pre-operative-declarations/${id}`);

			const transformedData = transformData<Declaration[]>(data, declarationMap);

			transformedData.forEach((declaration) => {
				window.open(declaration.declarationPath, "_blank");
			});
			setIsDeclarationLoading(false);
		} catch (error) {
			console.log(error);
			setIsDeclarationLoading(false);
		}
	}

	function getProcedureValue() {
		if (isQueueAdmission) {
			return {
				value: queueAdmissionData.expertiseId,
				label: queueAdmissionData.expertiseName,
			};
		}

		return { value: expertise?.id, label: expertise?.name };
	}

	useEffect(() => {
		if (!patientAdmissionModalShow) {
			setAttendance({} as SurgicalAttendance);
		}
	}, [patientAdmissionModalShow]);

	return (
		<Modal size="lg" show={patientAdmissionModalShow} onHide={handlePatientAdmissionModalClose}>
			<Modal.Header className="d-flex justify-content-center border-0">
				<Modal.Title>Admissão de Paciente em Leitos</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form
					id="admission-patient-form"
					className="d-flex flex-column gap-4"
					onSubmit={formik.handleSubmit}
				>
					<div>
						<Form.Label htmlFor="patient-name" className="fw-bold mb-2">
							Nome do Paciente
						</Form.Label>

						{isQueueAdmission ? (
							<ReactSelect
								value={{
									value: 1,
									label: queueAdmissionData.patientName,
								}}
								isDisabled
								isSolid
							/>
						) : (
							<PatientsSelect
								inputId="patient-name"
								onChange={(event) => setAttendance(event as SurgicalAttendance)}
							/>
						)}
					</div>

					<div>
						<Form.Label htmlFor="bed-allocation" className="fw-bold mb-2">
							Alocação em Leito Pré-Operatório
						</Form.Label>

						{isQueueAdmission ? (
							<BedsSelect
								inputId="bed-allocation"
								onChange={(option) =>
									formik.setFieldValue(
										"surgical_bed_id",
										(option as ReactSelectOption).value
									)
								}
							/>
						) : (
							<ReactSelect
								value={{ value: 1, label: bed?.fullName }}
								isDisabled
								isSolid
							/>
						)}
					</div>

					<div>
						<Form.Label className="fw-bold mb-2">Procedimento</Form.Label>

						<ReactSelect value={getProcedureValue()} isSolid isDisabled />
					</div>

					<div>
						<FormattedFormControl
							id="surgery-date"
							label="Data da Cirurgia"
							type="date"
							labelClassName="fw-bold"
							formattedFormControlClassName="form-control-solid"
							{...formik.getFieldProps("surgery_date")}
							name="surgery_date"
						/>
					</div>

					<div>
						<Form.Label htmlFor="surgical-team" className="fw-bold mb-2">
							Equipe
						</Form.Label>

						<ReactSelect
							inputId="surgical-team"
							options={surgicalTeamsOptions}
							onChange={(option) =>
								formik.setFieldValue(
									"surgical_team_id",
									(option as ReactSelectOption).value
								)
							}
							isSolid
						/>
					</div>

					<div>
						<FormGroupRadio
							name="procedure_size"
							label="Porte do procedimento"
							labelClassName="fw-bold"
							requestedValue={formik.values.procedure_size}
							onChange={formik.handleChange}
							inline
						>
							<FormRadio label="Pequeno" value="1" id="small" />
							<FormRadio label="Médio" value="2" id="medium" />
							<FormRadio label="Grande" value="3" id="big" />
						</FormGroupRadio>
					</div>

					<div className="mt-2">
						<Form.Label htmlFor="surgical-team" className="fw-bold mb-2">
							Declarações
						</Form.Label>
						<Row className={classNames(width < 992 && "gap-5")}>
							<DeclarationCard description="Declaração de Consentimento Esclarecido">
								<Button
									className="px-5 py-1"
									onClick={getAllDeclarations}
									disabled={
										(!isQueueAdmission &&
											!Boolean(Object.entries(attendance).length)) ||
										isDeclarationLoading
									}
								>
									{isDeclarationLoading ? (
										<Spinner
											as="span"
											animation="border"
											size="sm"
											role="status"
											aria-hidden="true"
										/>
									) : (
										"Ver"
									)}
								</Button>
							</DeclarationCard>
							<DeclarationCard description="Termo de Responsabilidade">
								<Button className="px-5 py-1">Ver</Button>
							</DeclarationCard>
						</Row>
					</div>
				</Form>
			</Modal.Body>

			<Modal.Footer className="d-flex justify-content-between border-0">
				<Button variant="secondary" className="py-2">
					<div className="d-flex align-items-center gap-1">
						AIH <RxExternalLink />
					</div>
				</Button>

				<div className="d-flex gap-2">
					<Button
						variant="secondary"
						className="py-2"
						onClick={handlePatientAdmissionModalClose}
					>
						Cancelar
					</Button>

					<Button
						type="submit"
						className="py-2"
						form="admission-patient-form"
						disabled={isPatientAdmissionModalLoading}
					>
						{isPatientAdmissionModalLoading ? (
							<Spinner
								as="span"
								animation="border"
								size="sm"
								role="status"
								aria-hidden="true"
							/>
						) : (
							"Confirmar"
						)}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
